import { useState, ChangeEvent } from "react";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ReactComponent as DriveIcon } from "../../../assets/driveIcon.svg";
import { ReactComponent as CloudIcon } from "../../../assets/cloudIcon.svg";
import { ReactComponent as CloudErrorIcon } from "../../../assets/cloudErrorIcon.svg";
import { addContentItemProvider } from "../../../api/admin/content-providers/endpoints-referrers";
import { Loader } from "../../../components/loader/loader.comp";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";

const LinkGoogleDriveView = ({
    handleClose,
    handleSuccess
}: {
    handleClose: () => void;
    handleSuccess: () => void;
}) => {
    const { user, knowledgeBases } = FirebaseAuthContainer.useContainer();
    const [driveLink, setDriveLink] = useState<string>("");
    const [driveName, setDriveName] = useState<string>("");
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const loadingDescription = `You can now close this window.
    Google Drive will appear when connection is complete.`;
    const completeDescription = `Files from this drive will appear soon.
    Depending on the size and quantity in may take some time.`;

    const handleCloseClick = () => {
        handleClose();
    };

    // const handleTryAgainClick = () => {
    //     setStep(LoaderStep.start);
    // };

    const handleDriveLinkChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setDriveLink(val ?? "");
    };

    const handleDriveNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setDriveName(val ?? "");
    };

    const handleConnectClick = () => {
        if (!user || !driveName || !driveLink || !knowledgeBases.length) return;

        // TODO: get from page path params. User should choose it on Knowledge Bases page
        const knowledgeBase = knowledgeBases[0];

        setStep(LoaderStep.loading);
        addContentItemProvider(user.companyId, {
            name: ProviderType.googleDrive,
            displayName: driveName,
            googleDriveUrl: driveLink,
            knowledgeBaseId: knowledgeBase.uid
        })
            .then(() => {
                setStep(LoaderStep.complete);
                handleSuccess();
            })
            .catch(error => {
                switch (error.code) {
                    case "ERR_NETWORK":
                        setErrorMessage("Network error.");
                        break;
                    case "ERR_BAD_REQUEST":
                        setErrorMessage(`There was a problem connecting Google Drive “${driveName}”.
                        Check its permissions for external access.`);
                        break;
                    default:
                        setErrorMessage("Something went wrong. Please try again later.");
                }
                setStep(LoaderStep.error);
            });
    };

    return (
        <div className="link-google-drive">
            {step === LoaderStep.start && (
                <>
                    <div className="link-google-drive-title">
                        <SvgIcon
                            className="link-google-drive-title__icon"
                            component={DriveIcon}
                            inheritViewBox
                        />
                        <span className="link-google-drive-title__text">Add Source</span>
                    </div>
                    <div className="link-google-drive-description">
                        <span className="link-google-drive-description__text">
                            You can add new source (“Google Drive” or “One Drive”)
                        </span>
                    </div>
                    <div className="link-google-drive-data">
                        <input
                            tabIndex={1}
                            type="text"
                            placeholder="Copy link to google drive here"
                            onChange={handleDriveLinkChange}
                            value={driveLink}
                        />
                        <input
                            tabIndex={2}
                            type="text"
                            placeholder="Name this drive (can be changed later)"
                            onChange={handleDriveNameChange}
                            value={driveName}
                        />
                    </div>
                </>
            )}
            {step === LoaderStep.loading && (
                <div className="link-source-loading">
                    <div className="link-source-loading-title">
                        <Loader
                            className="link-source-loading-title__spinner"
                            size={40}
                            borderWidth={5}
                        />
                        <span className="link-source-loading-title__text">
                            Connection in Progress
                        </span>
                    </div>
                    <span className="link-source-loading-description">{loadingDescription}</span>
                </div>
            )}
            {step === LoaderStep.complete && (
                <div className="link-source-complete">
                    <div className="link-source-complete-title">
                        <SvgIcon
                            className="link-source-complete-title__icon"
                            component={CloudIcon}
                            inheritViewBox
                        />
                        <span className="link-source-complete-title__text">
                            Google Drive Connected
                        </span>
                    </div>
                    <span className="link-source-complete-description">{completeDescription}</span>
                </div>
            )}
            {step === LoaderStep.error && (
                <div className="link-source-error">
                    <div className="link-source-error-title">
                        <SvgIcon
                            className="link-source-error-title__icon"
                            component={CloudErrorIcon}
                            inheritViewBox
                        />
                        <span className="link-source-error-title__text">Connection Error</span>
                    </div>
                    <span className="link-source-error-description">{errorMessage}</span>
                </div>
            )}
            <div className="link-google-drive-action">
                {(step === LoaderStep.start || step === LoaderStep.loading) && (
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleConnectClick}
                        disabled={!driveLink || !driveName || step === LoaderStep.loading}
                    >
                        {step === LoaderStep.start ? "Add" : "Please wait"}
                    </Button>
                )}
                {(step === LoaderStep.complete || step === LoaderStep.error) && (
                    <Button variant="text" className="primary-button" onClick={handleCloseClick}>
                        Close
                    </Button>
                )}
            </div>
        </div>
    );
};

export default LinkGoogleDriveView;
